/**
 * Push on this phone (docs/superpowers/specs/2026-10-31-mobile-app-part2-push-design.md): allowed only on an
 * action of the person (never at start), its token kept on this phone's record on the server, removed there
 * when the phone's settings no longer allow push, and a tapped push opening its page — in the push's company.
 * The person's own choice is kept on the phone (which phone record wants push): a start refreshes the token
 * only while push is wanted, so "Turn off" stays off, and a phone that lets apps push without asking (Android
 * 12 and older) gets nothing until the person allows push here.
 */
import { authApi } from '../api'
import { useAuthStore } from '../../store/auth'
import { appBridge } from './bridge'

const WANTED = 'velanox.pushWanted'
const wanted = () => { try { return localStorage.getItem(WANTED) } catch { return null } }
const want = (id: string | null) => {
  try { if (id) localStorage.setItem(WANTED, id); else localStorage.removeItem(WANTED) } catch { /* then nothing is refreshed at start */ }
}
/** the server's own words (in the person's language), for a refusal */
const refusal = (e: unknown, fallback: string) => new Error((e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? fallback)

/** This phone's record on this server, when it stays signed in and the app offers push */
async function thisPhone() {
  const app = appBridge()
  if (!app?.features.push || !app.allowPush || !app.pushToken) return null
  const id = await app.storedPhone()
  return id ? { app, id } : null
}

/**
 * The phone's question, then the token on the server; false when the phone refused. A refusal by the server
 * (a phone added in another place) is thrown with its reason.
 */
export async function allowPushHere(channelName: string): Promise<boolean> {
  const p = await thisPhone()
  if (!p) return false
  const token = await p.app.allowPush!(channelName)
  if (!token) return false
  try { await authApi.setPush(p.id, token) } catch (e) { throw refusal(e, 'Push could not be allowed') }
  want(p.id)
  return true
}

/** Push turned off on this phone, and kept off */
export async function turnOffPushHere(): Promise<void> {
  const p = await thisPhone()
  want(null)
  if (p) await authApi.clearPush(p.id)
}

/**
 * At start and when Firebase hands out a new token, while the person wants push on this phone: allowed by the
 * phone → the token on the server (and Android's channel there); refused in the phone's settings → off on the
 * server too. Nothing is asked of the person here.
 */
export async function syncPush(channelName: string): Promise<void> {
  const p = await thisPhone()
  if (!p || wanted() !== p.id) return
  const permission = await p.app.pushPermission?.()
  if (permission === 'granted') {
    // already allowed: no question; the token, and the channel made if it is not there
    const token = await p.app.allowPush!(channelName)
    if (token) await authApi.setPush(p.id, token).catch(() => undefined)
  } else if (permission === 'denied') {
    await authApi.clearPush(p.id).catch(() => undefined)
  }
}

/**
 * A tapped push's page: on this server only; `company` (a staff push) switches to that company first
 * when the app is in another of the person's companies.
 */
export async function openPushPath(path: string, navigate: (to: string) => void): Promise<void> {
  if (!path.startsWith('/') || path.startsWith('//')) return
  const url = new URL(path, window.location.origin)
  if (url.origin !== window.location.origin) return
  const company = url.searchParams.get('company')
  url.searchParams.delete('company')
  const { activeCompanyId, setActiveCompany } = useAuthStore.getState()
  if (company && company !== activeCompanyId) {
    try {
      const { accessToken, activeCompanyId: active, membershipId } = (await authApi.switchCompany(company)).data.data
      setActiveCompany({ accessToken, activeCompanyId: active, membershipId })
    } catch { /* not one of theirs (any more): the page says so */ }
  }
  navigate(url.pathname + url.search + url.hash)
}
